import {PropsWithChildren, useEffect, useMemo} from 'react';

import {Routes, Route, useParams, Navigate} from 'react-router-dom';

import {
  getEnabledFeaturesLoadingStatus,
  getIsBuildingsLoaded,
  getIsLoadingSettings,
  getIsParkingFeatureEnabled,
  getIsSignedIn,
  getMeetingPreferencesLoadingStatus,
  getUser,
  loadAppData,
  loadNotifications,
  loadParkingLots,
  withAsyncThunkErrorHandling,
  getAllCalendarDays,
  loadParkingCountByDateRange,
} from '@lib/store';
import {
  useAppSelector,
  useAppDispatch,
  useDetectRedirect,
  useCalculateCssVhVariable,
  useFeatureToggle,
  useAlertDialog,
  useMsTeams,
  useIsTeamsApp,
} from '@hooks';

import {Canvas, Layout} from '@templates';

import {Dialog, Footer, Header, Modal, SidePanel, TeamsSignIn, TeamsSignOut} from '@organisms';
import {
  Calendar,
  Connections,
  Notifications,
  NotFound,
  Onboarding,
  PrivateRoute,
  Profile,
  HereAndNow,
  AppLinks,
} from '@pages';
import {Toaster} from '@molecules';
import {AppInsights} from './config/AppInsights';
import {useMsal} from '@azure/msal-react';
import {useShowErrorToUser} from './hooks/useShowErrorToUser';
import * as IntercomServices from '@utils';
import {Loader} from './components/atoms/Loader';
import format from 'date-fns/format';
import {Home} from './components/pages/Home';
import {HaNRedirect} from './components/HaNRedirect';
import {PageId, trackNavigateToPage} from '@lib/infrastructure';
import {TeamsCalendarConnect} from './components/organisms/TeamsCalendarConnect';

let isFirstPageOfSession = true;

export const App = () => {
  const dispatch = useAppDispatch();
  const {instance} = useMsal();
  const signedInUser = instance.getActiveAccount();
  const subId = signedInUser?.idTokenClaims?.subscription_id || 'failed-to-get-sub-id-from-token';
  const userId = signedInUser?.idTokenClaims?.sub || 'failed-to-get-user-id-from-token';
  const {AppLinks: isAppLinksEnabled} = useFeatureToggle();
  const user = useAppSelector(getUser);
  const tokenClaims = signedInUser?.idTokenClaims;
  const isSignedIn = useAppSelector(getIsSignedIn);
  const flagsLoadingStatus = useAppSelector(getEnabledFeaturesLoadingStatus);
  const days = useAppSelector((state) => getAllCalendarDays(state));

  const isTeamsApp = useIsTeamsApp();

  const firstBookableDay = days.at(0)?.date;
  const lastBookableDay = days.at(-1)?.date || firstBookableDay;

  const isParkingFeatureEnabled = useAppSelector(getIsParkingFeatureEnabled);

  useMsTeams();
  useDetectRedirect();
  useCalculateCssVhVariable();
  // Handle errors thrown by api calls
  useShowErrorToUser();

  useEffect(() => {
    if (isSignedIn) {
      AppInsights.setAuthenticatedUserContext(userId, subId as string);

      IntercomServices.login(tokenClaims, user);
    } else {
      AppInsights.clearAuthenticatedUserContext();

      IntercomServices.logout();
    }
  }, [isSignedIn, subId, userId, tokenClaims, user]);

  useEffect(() => {
    if (isSignedIn) {
      dispatch(withAsyncThunkErrorHandling(() => loadAppData()));
    }
  }, [dispatch, isSignedIn]);

  useEffect(() => {
    // This is the initial load of the notifications to avoid flickering
    // of the notification route when the side panel is openned
    // false is for the isPollMode param
    if (isSignedIn) dispatch(withAsyncThunkErrorHandling(() => loadNotifications()));
  }, [dispatch, isSignedIn]);

  useEffect(
    function loadAllParkingLots() {
      dispatch(withAsyncThunkErrorHandling(() => loadParkingLots()));
    },
    [dispatch, isParkingFeatureEnabled],
  );

  useEffect(
    function getParkingReservationCounts() {
      if (firstBookableDay && lastBookableDay) {
        dispatch(
          withAsyncThunkErrorHandling(() => loadParkingCountByDateRange({from: firstBookableDay, to: lastBookableDay})),
        );
      }
    },
    [dispatch, firstBookableDay, lastBookableDay],
  );

  const calendarIsBroken = useAppSelector((state) => {
    const workdayEntries = Object.values(state.data.timeslots.workdaysByDate);
    return workdayEntries.length > 0 && workdayEntries.every((e) => e?.status === 'rejected');
  });

  const alert = useAlertDialog();

  useEffect(
    function showAlert() {
      if (calendarIsBroken) {
        alert.alertDialog(
          'Failed to load workdays',
          'We were unable to retrieve your workdays. Your calendar will look empty',
        );
      }
    },
    [alert.alertDialog, calendarIsBroken],
  );

  return (
    <Layout $isTeamsApp={isTeamsApp}>
      <Header />
      <Routes>
        {/* This route handles silent login flows. It will only be used by hidden iframes. It will not trigger any MSAL stuff. */}
        <Route
          path="silent-login"
          element={<h1>Silent login, welcome back</h1>}
        />

        <Route
          path="teams-signin"
          element={<TeamsSignIn />}
        />
        <Route
          path="teams-signout"
          element={<TeamsSignOut />}
        />
        <Route
          path="teams-calendar-connect"
          element={<TeamsCalendarConnect />}
        />

        <Route element={<PrivateRoute />}>
          {flagsLoadingStatus === 'Loading' || flagsLoadingStatus === 'NotLoaded' ? (
            <Route
              path="*"
              element={<WithPrerequisites />}
            />
          ) : (
            <Route
              element={
                <WithPrerequisites>
                  <Onboarding />
                </WithPrerequisites>
              }>
              {MainRoutes(!!isAppLinksEnabled)}
            </Route>
          )}
        </Route>

        <Route
          path="fourohfour"
          element={<NotFound />}
        />
      </Routes>

      <Footer />
      <SidePanel />
      <Modal />
      <Dialog />
      <Toaster />
    </Layout>
  );
};

const MainRoutes = (isAppLinksEnabled: boolean) => {
  return (
    <>
      <Route
        path="/l/:appLinkId"
        element={
          isAppLinksEnabled ? (
            <TrackNavigation pageId="appLinks">
              <WithPrerequisites>
                <AppLinks />
              </WithPrerequisites>
            </TrackNavigation>
          ) : (
            <Navigate to="/fourohfour" />
          )
        }
      />

      <Route
        path="/"
        element={
          <TrackNavigation pageId="home">
            <WithPrerequisites>
              <Home />
            </WithPrerequisites>
          </TrackNavigation>
        }
      />

      <Route
        path="/calendar/:date?"
        element={
          <TrackNavigation pageId="calendar">
            <WithPrerequisites>
              <Calendar />
            </WithPrerequisites>
          </TrackNavigation>
        }
      />

      <Route>
        <Route
          path="/now"
          element={<HaNRedirect />}
        />
        <Route
          path="/now/rooms"
          element={
            <TrackNavigation pageId="hereAndNowRooms">
              <WithPrerequisites>
                <HereAndNow tab="rooms" />
              </WithPrerequisites>
            </TrackNavigation>
          }
        />
        <Route
          path="/now/workspaces"
          element={
            <TrackNavigation pageId="hereAndNowWorkspaces">
              <WithPrerequisites>
                <HereAndNow tab="workspaces" />
              </WithPrerequisites>
            </TrackNavigation>
          }
        />
        <Route
          path="/now/explore"
          element={
            <TrackNavigation pageId="hereAndNowExplore">
              <WithPrerequisites>
                <HereAndNow tab="explore" />
              </WithPrerequisites>
            </TrackNavigation>
          }
        />
      </Route>

      <Route
        path="connections"
        element={
          <TrackNavigation pageId="connections">
            <WithPrerequisites>
              <Connections />
            </WithPrerequisites>
          </TrackNavigation>
        }
      />
      <Route
        path="notifications"
        element={
          <TrackNavigation pageId="notifications">
            <WithPrerequisites>
              <Notifications />
            </WithPrerequisites>
          </TrackNavigation>
        }
      />
      <Route
        path="profile"
        element={
          <TrackNavigation pageId="profile">
            <WithPrerequisites>
              <Profile />
            </WithPrerequisites>
          </TrackNavigation>
        }
      />
      <Route
        path="/:maybeDate"
        element={<DateRedirect />}
      />
    </>
  );
};

const TrackNavigation = ({children, pageId}: PropsWithChildren<{pageId: PageId}>) => {
  trackNavigateToPage(pageId, isFirstPageOfSession);
  if (isFirstPageOfSession) isFirstPageOfSession = false;
  return children;
};

const WithPrerequisites = ({children, noLoader}: PropsWithChildren<{noLoader?: boolean}>) => {
  const meetingLoadingStatus = useAppSelector(getMeetingPreferencesLoadingStatus);
  const buildingsHaveLoaded = useAppSelector(getIsBuildingsLoaded);
  const loadingSettings = useAppSelector(getIsLoadingSettings) ?? true;
  const enabledFeaturesLoadingStatus = useAppSelector(getEnabledFeaturesLoadingStatus);

  const {HybridMeetingsCalendarView} = useFeatureToggle();

  const loadedAllPrerequisites = useMemo(() => {
    if (enabledFeaturesLoadingStatus === 'Loaded' && !loadingSettings && buildingsHaveLoaded) {
      if (HybridMeetingsCalendarView) {
        return meetingLoadingStatus === 'Loaded';
      } else {
        return true;
      }
    }

    return false;
  }, [
    buildingsHaveLoaded,
    HybridMeetingsCalendarView,
    enabledFeaturesLoadingStatus,
    loadingSettings,
    meetingLoadingStatus,
  ]);

  return loadedAllPrerequisites ? (
    children
  ) : noLoader ? null : (
    <Canvas centerContent>
      <Loader />
    </Canvas>
  );
};

/*
  This component serves to smoothen the transition to the new home page. 
  Previously, URLs of the format /yyyy-MM-dd would show a calendar day.
  With the introduction of the AppHomePage feature flag, those pages have
  been moved to /calendar/yyyy-MM-dd.
  
  This redirect looks at any url of /some-string that has not been claimed
  by a different route. If the some-string is a valid date format, it redirects
  to /calendar/some-string. If not, it 404s.
*/
const DateRedirect = () => {
  const {maybeDate} = useParams();

  if (!maybeDate) {
    throw new Error('Invalid use of DateRedirect route; should only be called with maybeDate parameter.');
  }

  const date = new Date(maybeDate);
  if (isNaN(date.getTime())) {
    return <Navigate to="/fourohfour" />;
  }

  return (
    <Navigate
      to={`/calendar/${format(date, 'yyyy-MM-dd')}`}
      replace={true}
    />
  );
};
